import type { VehicleInsurance } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrencyForDisplay } from "../../../../../components/ui/uikit/currencyInput";
import { CurrencyField, DateField, FullCoverageField, LossPayeeField, PhoneField, TextField } from "./Fields";
import {
  AGENT_NAME_MAX_LENGTH,
  buildInsuranceDefaultValues,
  type FullCoverageValue,
  insuranceInformationSchema,
  POLICY_NUMBER_MAX_LENGTH,
} from "./InsuranceInformation.schema";

interface InsuranceInformationProperties {
  readonly insurance?: VehicleInsurance;
  /** From `financial.amountFinanced` on the dashboard response — not part of `VehicleInsurance` itself. */
  readonly amountFinanced?: number;
  /** Whether every field below Amount Financed is typeable/selectable — condition TBD; defaults to disabled (today's behavior). */
  readonly editable?: boolean;
}

/**
 * RHF + Zod, same pattern as ContractVehicleInformation.tsx — see
 * InsuranceInformation.schema.ts for the validation rules. Unlike that spike,
 * every field here except Full Coverage already has a real backing value on
 * `VehicleInsurance` today, so `defaultValues` are built from the real
 * `insurance` prop rather than a fully-static mock.
 *
 * Not registered with `useRegisterSaveableSection` — `ContractVehicleInformation`
 * already contributes both `vehicle` and `vehicleInsurance` (the latter carrying Lic Plate,
 * which has no slot on `VehicleSaveRequestV2`), and `useSaveAllSections`'s merge is a
 * shallow last-wins `Object.assign` — a second section also writing to either key
 * would silently clobber one or the other's data rather than combine. Wiring this
 * section's save (OR-25050) has to merge `vehicleInsurance`, not overwrite it.
 */
export function InsuranceInformation({ insurance, amountFinanced, editable = false }: InsuranceInformationProperties) {
  const { t } = useTranslation();
  const disabled = !editable;
  const form = useForm({
    resolver: zodResolver(insuranceInformationSchema),
    mode: "onBlur",
    defaultValues: buildInsuranceDefaultValues(insurance),
  });
  const { control } = form;
  const fullCoverage = useController({ control, name: "fullCoverage" });
  const policyNumber = useController({ control, name: "policyNumber" });
  const insurerName = useController({ control, name: "insurerName" });
  const insurerPhone = useController({ control, name: "insurerPhone" });
  const agentName = useController({ control, name: "agentName" });
  const expirationDate = useController({ control, name: "expirationDate" });
  const comprehensiveDeductibleAmount = useController({ control, name: "comprehensiveDeductibleAmount" });
  const collisionDeductibleAmount = useController({ control, name: "collisionDeductibleAmount" });
  const isCaLossPayee = useController({ control, name: "isCaLossPayee" });

  const deductiblesDisabled = disabled || fullCoverage.field.value !== "Y";

  // Selecting anything other than "Yes" disables and immediately nulls out both deductibles.
  // `null`, not `undefined` — see the schema's comment on these two fields for why.
  function handleFullCoverageChange(value: FullCoverageValue) {
    fullCoverage.field.onChange(value);
    if (value === "Y") {
      void form.trigger(["comprehensiveDeductibleAmount", "collisionDeductibleAmount"]);
    } else {
      comprehensiveDeductibleAmount.field.onChange(null);
      collisionDeductibleAmount.field.onChange(null);
      void form.trigger(["comprehensiveDeductibleAmount", "collisionDeductibleAmount"]);
    }
  }

  // A new company name invalidates the phone that belonged to the previous one.
  function handleInsurerNameChange(value: string) {
    insurerName.field.onChange(value);
    insurerPhone.field.onChange("");
  }

  return (
    <div>
      <SubsectionHeader>{t("vehicle.insurance.heading")}</SubsectionHeader>
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <LabeledValue
              label={t("vehicle.insurance.amountFinanced")}
              value={formatCurrencyForDisplay(amountFinanced)}
            />
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              {fullCoverage.field.value !== undefined && (
                <FullCoverageField
                  label={t("vehicle.insurance.fullCoverage")}
                  value={fullCoverage.field.value}
                  onChange={handleFullCoverageChange}
                  disabled={disabled}
                />
              )}
              <TextField
                label={t("vehicle.insurance.policyNumber")}
                value={policyNumber.field.value}
                onChange={policyNumber.field.onChange}
                onBlur={policyNumber.field.onBlur}
                error={policyNumber.fieldState.error}
                disabled={disabled}
                maxLength={POLICY_NUMBER_MAX_LENGTH}
              />
            </div>
            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2">
              <TextField
                label={t("vehicle.insurance.companyName")}
                value={insurerName.field.value}
                onChange={handleInsurerNameChange}
                onBlur={insurerName.field.onBlur}
                error={insurerName.fieldState.error}
                disabled={disabled}
              />
              <PhoneField
                label={t("vehicle.insurance.companyPhone")}
                value={insurerPhone.field.value}
                onChange={insurerPhone.field.onChange}
                onBlur={insurerPhone.field.onBlur}
                error={insurerPhone.fieldState.error}
                disabled={disabled}
              />
              <TextField
                label={t("vehicle.insurance.agent")}
                value={agentName.field.value}
                onChange={agentName.field.onChange}
                onBlur={agentName.field.onBlur}
                error={agentName.fieldState.error}
                disabled={disabled}
                maxLength={AGENT_NAME_MAX_LENGTH}
                uppercase
              />
              <DateField
                label={t("vehicle.insurance.expirationDate")}
                value={expirationDate.field.value}
                onChange={expirationDate.field.onChange}
                onBlur={expirationDate.field.onBlur}
                disabled={disabled}
              />
              <CurrencyField
                label={t("vehicle.insurance.comprehensiveDeductible")}
                value={comprehensiveDeductibleAmount.field.value}
                onChange={comprehensiveDeductibleAmount.field.onChange}
                onBlur={comprehensiveDeductibleAmount.field.onBlur}
                error={comprehensiveDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
              />
              <CurrencyField
                label={t("vehicle.insurance.collisionDeductible")}
                value={collisionDeductibleAmount.field.value}
                onChange={collisionDeductibleAmount.field.onChange}
                onBlur={collisionDeductibleAmount.field.onBlur}
                error={collisionDeductibleAmount.fieldState.error}
                disabled={deductiblesDisabled}
              />
              <LossPayeeField
                label={t("vehicle.insurance.caLossPayee")}
                value={isCaLossPayee.field.value}
                onChange={isCaLossPayee.field.onChange}
                disabled={disabled}
              />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
